import React, { useState } from 'react';

const SalaryIncomeForm = ({ data, updateData, personalInfo, updatePersonalInfo, nextStep, prevStep }) => {
    const [formData, setFormData] = useState({
        // A. Fixed / Committed Expenses
        rentEMI: data?.rentEMI || '',
        societyMaintenance: data?.societyMaintenance || '',
        propertyTax: data?.propertyTax || '',
        propertyTaxAmount: data?.propertyTaxAmount || '',
        propertyTaxFrequency: data?.propertyTaxFrequency || 'annually',

        // Loan Repayments
        homeLoanEMI: data?.homeLoanEMI || '',
        carLoanEMI: data?.carLoanEMI || '',
        personalLoanEMI: data?.personalLoanEMI || '',
        educationLoanEMI: data?.educationLoanEMI || '',
        creditCardEMI: data?.creditCardEMI || '',
        otherLoanEMI: data?.otherLoanEMI || '',

        // Insurance Premiums
        lifeInsurance: data?.lifeInsurance || '',
        lifeInsuranceAmount: data?.lifeInsuranceAmount || '',
        lifeInsuranceFrequency: data?.lifeInsuranceFrequency || 'monthly',
        lifeInsuranceCover: data?.lifeInsuranceCover || '',
        healthInsurance: data?.healthInsurance || '',
        healthInsuranceAmount: data?.healthInsuranceAmount || '',
        healthInsuranceFrequency: data?.healthInsuranceFrequency || 'annually',
        healthInsuranceCover: data?.healthInsuranceCover || '',
        criticalIllness: data?.criticalIllness || '',
        motorInsurance: data?.motorInsurance || '',
        motorInsuranceAmount: data?.motorInsuranceAmount || '',
        motorInsuranceFrequency: data?.motorInsuranceFrequency || 'annually',
        homeInsurance: data?.homeInsurance || '',
        homeInsuranceAmount: data?.homeInsuranceAmount || '',
        homeInsuranceFrequency: data?.homeInsuranceFrequency || 'annually',

        // Mandatory Contributions
        epf: data?.epf || '',
        vpf: data?.vpf || '',
        ppf: data?.ppf || '',
        nps_80ccd2: data?.nps_80ccd2 || '',
        sukanyaSamriddhi: data?.sukanyaSamriddhi || '',

        // Utilities
        electricity: data?.electricity || '',
        water: data?.water || '',
        gas: data?.gas || '',
        internet: data?.internet || '',
        broadband: data?.broadband || '',
        ottSubscriptions: data?.ottSubscriptions || '',
        cable: data?.cable || '',
    });

    const [salaryData, setSalaryData] = useState({
        monthlySalary: personalInfo?.monthlySalary || '',
        otherIncomeAmount: personalInfo?.otherIncomeAmount || '',
        otherIncomeType: personalInfo?.otherIncomeType || '',
        otherIncomes: personalInfo?.otherIncomes || (personalInfo?.otherIncomeAmount ? [{ type: personalInfo?.otherIncomeType || '', amount: personalInfo?.otherIncomeAmount }] : [{ type: '', amount: '' }])
    });

    const [expandedSections, setExpandedSections] = useState({
        income: true,
        housing: true,
        loans: false,
        insurance: false,
        contributions: false,
        utilities: false,
    });

    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSalaryChange = (e) => {
        const { name, value } = e.target;
        const newSalaryData = { ...salaryData, [name]: value };
        setSalaryData(newSalaryData);
        updatePersonalInfo({ ...personalInfo, ...newSalaryData });
        if (name === 'monthlySalary' && value) setError('');
    };

    const handleAddOtherIncome = () => {
        const newIncomes = [...(salaryData.otherIncomes || []), { type: '', amount: '' }];
        const newSalaryData = { ...salaryData, otherIncomes: newIncomes };
        setSalaryData(newSalaryData);
        updatePersonalInfo({ ...personalInfo, ...newSalaryData });
    };

    const handleRemoveOtherIncome = (index) => {
        const newIncomes = [...(salaryData.otherIncomes || [])];
        newIncomes.splice(index, 1);
        
        const totalAmount = newIncomes.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
        const types = newIncomes.map(i => i.type).filter(Boolean).join(', ');
        
        const newSalaryData = {
            ...salaryData,
            otherIncomes: newIncomes,
            otherIncomeAmount: totalAmount > 0 ? totalAmount.toString() : '',
            otherIncomeType: types
        };
        setSalaryData(newSalaryData);
        updatePersonalInfo({ ...personalInfo, ...newSalaryData });
    };

    const handleOtherIncomeItemChange = (index, field, value) => {
        const newIncomes = [...(salaryData.otherIncomes || [])];
        newIncomes[index] = { ...newIncomes[index], [field]: value };
        
        const totalAmount = newIncomes.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
        const types = newIncomes.map(i => i.type).filter(Boolean).join(', ');
        
        const newSalaryData = {
            ...salaryData,
            otherIncomes: newIncomes,
            otherIncomeAmount: totalAmount > 0 ? totalAmount.toString() : '',
            otherIncomeType: types
        };
        setSalaryData(newSalaryData);
        updatePersonalInfo({ ...personalInfo, ...newSalaryData });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!salaryData.monthlySalary) {
            setError('Monthly Salary is required');
            return;
        }
        updateData(formData);
        nextStep();
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const handleLifeInsuranceChange = (field, value) => {
        const newFormData = { ...formData, [field]: value };
        
        // Calculate monthly value based on frequency
        if (field === 'lifeInsuranceAmount' || field === 'lifeInsuranceFrequency') {
            const amount = parseFloat(newFormData.lifeInsuranceAmount) || 0;
            const frequency = newFormData.lifeInsuranceFrequency;
            let monthlyValue = 0;
            
            switch (frequency) {
                case 'monthly':
                    monthlyValue = amount;
                    break;
                case 'quarterly':
                    monthlyValue = amount / 3;
                    break;
                case 'halfYearly':
                    monthlyValue = amount / 6;
                    break;
                case 'annually':
                    monthlyValue = amount / 12;
                    break;
                default:
                    monthlyValue = amount;
            }
            
            newFormData.lifeInsurance = monthlyValue > 0 ? monthlyValue.toFixed(2) : '';
        }
        
        setFormData(newFormData);
    };

    // Health Insurance with Monthly/Annually frequency
    const handleHealthInsuranceChange = (field, value) => {
        const newFormData = { ...formData, [field]: value };
        
        if (field === 'healthInsuranceAmount' || field === 'healthInsuranceFrequency') {
            const amount = parseFloat(newFormData.healthInsuranceAmount) || 0;
            const frequency = newFormData.healthInsuranceFrequency || 'annually';
            
            let monthlyValue = 0;
            switch (frequency) {
                case 'monthly':
                    monthlyValue = amount;
                    break;
                case 'annually':
                default:
                    monthlyValue = amount / 12;
            }
            
            newFormData.healthInsurance = monthlyValue > 0 ? monthlyValue.toFixed(2) : '';
        }
        
        setFormData(newFormData);
    };

    // Property Tax with Monthly/Annually frequency
    const handlePropertyTaxChange = (field, value) => {
        const newFormData = { ...formData, [field]: value };
        
        if (field === 'propertyTaxAmount' || field === 'propertyTaxFrequency') {
            const amount = parseFloat(newFormData.propertyTaxAmount) || 0;
            const frequency = newFormData.propertyTaxFrequency || 'annually';
            
            let monthlyValue = 0;
            switch (frequency) {
                case 'monthly':
                    monthlyValue = amount;
                    break;
                case 'annually':
                default:
                    monthlyValue = amount / 12;
            }
            
            newFormData.propertyTax = monthlyValue > 0 ? monthlyValue.toFixed(2) : '';
        }
        
        setFormData(newFormData);
    };

    // Motor Insurance with Monthly/Annually frequency
    const handleMotorInsuranceChange = (field, value) => {
        const newFormData = { ...formData, [field]: value };
        
        if (field === 'motorInsuranceAmount' || field === 'motorInsuranceFrequency') {
            const amount = parseFloat(newFormData.motorInsuranceAmount) || 0;
            const frequency = newFormData.motorInsuranceFrequency || 'annually';
            
            let monthlyValue = 0;
            switch (frequency) {
                case 'monthly':
                    monthlyValue = amount;
                    break;
                case 'annually':
                default:
                    monthlyValue = amount / 12;
            }
            
            newFormData.motorInsurance = monthlyValue > 0 ? monthlyValue.toFixed(2) : '';
        }
        
        setFormData(newFormData);
    };

    // Home Insurance with Monthly/Annually frequency
    const handleHomeInsuranceChange = (field, value) => {
        const newFormData = { ...formData, [field]: value };
        
        if (field === 'homeInsuranceAmount' || field === 'homeInsuranceFrequency') {
            const amount = parseFloat(newFormData.homeInsuranceAmount) || 0;
            const frequency = newFormData.homeInsuranceFrequency || 'annually';
            
            let monthlyValue = 0;
            switch (frequency) {
                case 'monthly':
                    monthlyValue = amount;
                    break;
                case 'annually':
                default:
                    monthlyValue = amount / 12;
            }
            
            newFormData.homeInsurance = monthlyValue > 0 ? monthlyValue.toFixed(2) : '';
        }
        
        setFormData(newFormData);
    };

    const totalIncome = (parseFloat(salaryData.monthlySalary) || 0) + (parseFloat(salaryData.otherIncomeAmount) || 0);

    return (
        <div className="form-step">
            <h2 className="step-title">Part 1: Income & Fixed Expenses</h2>
            <p className="step-description">Your income details and essential committed expenses</p>

            <form onSubmit={handleSubmit} className="diagnosis-form">

                {/* Income Section */}
                <div className="expense-section" style={{ background: '#e0f2fe', border: '1px solid #bae6fd' }}>
                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('income')}>
                        <span>1. Income Details</span>
                        <span className="toggle-icon">{expandedSections.income ? '▼' : '▶'}</span>
                    </h4>
                    {expandedSections.income && (
                        <>
                            <div className="form-group">
                                <label style={{ fontSize: '0.9rem', marginBottom: '4px', display: 'block', fontWeight: '600', color: '#0369a1' }}>Monthly Gross Salary *</label>
                                <div className="input-with-icon" style={{ position: 'relative' }}>
                                    <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#0369a1' }}>₹</span>
                                    <input
                                        type="number"
                                        name="monthlySalary"
                                        value={salaryData.monthlySalary}
                                        onChange={handleSalaryChange}
                                        placeholder="Enter Amount"
                                        style={{ paddingLeft: '25px', width: '100%', padding: '10px 10px 10px 25px', borderRadius: '6px', border: error ? '1px solid red' : '1px solid #7dd3fc' }}
                                        onWheel={(e) => {
                                            e.target.blur();
                                            e.stopPropagation();
                                        }}
                                        onKeyDown={(e) => {
                                            if (['e', 'E', '+', '-', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                    />
                                </div>
                                {error && <span style={{ color: 'red', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{error}</span>}
                            </div>

                            <div className="form-group" style={{ marginTop: '15px' }}>
                                <label style={{ fontSize: '0.9rem', marginBottom: '8px', display: 'block', color: '#0369a1' }}>Other Income (Optional)</label>
                                {(salaryData.otherIncomes || [{ type: '', amount: '' }]).map((item, index) => (
                                    <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                                        <div style={{ position: 'relative', flex: 1 }}>
                                            <input
                                                list="otherIncomeSources"
                                                value={item.type}
                                                onChange={(e) => handleOtherIncomeItemChange(index, 'type', e.target.value)}
                                                placeholder="Source (Select or Type)"
                                                autoComplete="off"
                                                style={{ 
                                                    padding: '8px', 
                                                    borderRadius: '6px', 
                                                    border: '1px solid #7dd3fc', 
                                                    width: '100%',
                                                    height: '42px',
                                                    fontSize: '0.9rem'
                                                }}
                                            />
                                            <datalist id="otherIncomeSources">
                                                <option value="Rent" />
                                                <option value="Freelance" />
                                                <option value="Interest" />
                                                <option value="House Property" />
                                                <option value="Business" />
                                                <option value="Agricultural" />
                                                <option value="Capital Gains" />
                                            </datalist>
                                        </div>
                                        <div className="input-with-icon" style={{ position: 'relative', flex: 1 }}>
                                            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#0369a1' }}>₹</span>
                                            <input
                                                type="number"
                                                value={item.amount}
                                                onChange={(e) => handleOtherIncomeItemChange(index, 'amount', e.target.value)}
                                                placeholder="Amount"
                                                style={{ paddingLeft: '25px', width: '100%', padding: '8px 10px 8px 25px', borderRadius: '6px', border: '1px solid #7dd3fc', height: '42px' }}
                                                onWheel={(e) => {
                                                    e.target.blur();
                                                    e.stopPropagation();
                                                }}
                                                onKeyDown={(e) => {
                                                    if (['e', 'E', '+', '-', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                        </div>
                                        {(salaryData.otherIncomes && salaryData.otherIncomes.length > 1) && (
                                            <button type="button" onClick={() => handleRemoveOtherIncome(index)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1.5rem', padding: '0 8px' }} title="Remove">&times;</button>
                                        )}
                                    </div>
                                ))}
                                <button type="button" onClick={handleAddOtherIncome} style={{ background: 'none', border: 'none', color: '#0284c7', fontSize: '0.85rem', cursor: 'pointer', padding: 0, marginTop: '4px', fontWeight: '500' }}>+ Add More Income</button>
                            </div>

                            <div style={{ marginTop: '15px', borderTop: '1px dashed #7dd3fc', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', color: '#0369a1' }}>
                                <span>Total Monthly Income:</span>
                                <span>₹ {totalIncome.toLocaleString()}</span>
                            </div>
                        </>
                    )}
                </div>

                {/* Housing */}
                <div className="expense-section">
                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('housing')}>
                        <span>2. Housing</span>
                        <span className="toggle-icon">{expandedSections.housing ? '▼' : '▶'}</span>
                    </h4>
                    {expandedSections.housing && (
                        <>
                            <InputGroup label="Monthly Rent (If Rented)" name="rentEMI" value={formData.rentEMI} onChange={handleChange} />
                            <InputGroup label="Society Maintenance" name="societyMaintenance" value={formData.societyMaintenance} onChange={handleChange} />
                            <PropertyTaxInput
                                amount={formData.propertyTaxAmount}
                                frequency={formData.propertyTaxFrequency}
                                onChange={handlePropertyTaxChange}
                            />
                        </>
                    )}
                </div>

                {/* Loan Repayments */}
                <div className="expense-section">
                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('loans')}>
                        <span>3. Loan Repayments</span>
                        <span className="toggle-icon">{expandedSections.loans ? '▼' : '▶'}</span>
                    </h4>
                    {expandedSections.loans && (
                        <>
                            <InputGroup label="Home Loan EMI (Primary Residence)" name="homeLoanEMI" value={formData.homeLoanEMI} onChange={handleChange} />
                            <InputGroup label="Car Loan EMI" name="carLoanEMI" value={formData.carLoanEMI} onChange={handleChange} />
                            <InputGroup label="Personal Loan EMI" name="personalLoanEMI" value={formData.personalLoanEMI} onChange={handleChange} />
                            <InputGroup label="Education Loan EMI" name="educationLoanEMI" value={formData.educationLoanEMI} onChange={handleChange} />
                            <InputGroup label="Credit Card Outstanding (Minimum Due)" name="creditCardEMI" value={formData.creditCardEMI} onChange={handleChange} />
                            <InputGroup label="Any Other Loan EMI" name="otherLoanEMI" value={formData.otherLoanEMI} onChange={handleChange} />
                        </>
                    )}
                </div>

                {/* Insurance Premiums */}
                <div className="expense-section">
                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('insurance')}>
                        <span>4. Insurance Premiums</span>
                        <span className="toggle-icon">{expandedSections.insurance ? '▼' : '▶'}</span>
                    </h4>
                    {expandedSections.insurance && (
                        <>
                            {/* Life Insurance with Frequency Options */}
                            <LifeInsuranceInput 
                                amount={formData.lifeInsuranceAmount}
                                frequency={formData.lifeInsuranceFrequency}
                                monthlyValue={formData.lifeInsurance}
                                onChange={handleLifeInsuranceChange}
                            />
                            <InputGroup label="Total Existing Life Insurance Cover Amount" name="lifeInsuranceCover" value={formData.lifeInsuranceCover} onChange={handleChange} />
                            <HealthInsuranceInput
                                amount={formData.healthInsuranceAmount}
                                frequency={formData.healthInsuranceFrequency}
                                onChange={handleHealthInsuranceChange}
                            />
                            <InputGroup label="Total Existing Health Insurance Cover Amount" name="healthInsuranceCover" value={formData.healthInsuranceCover} onChange={handleChange} />
                            <InputGroup label="Critical Illness Insurance" name="criticalIllness" value={formData.criticalIllness} onChange={handleChange} />
                            <MotorInsuranceInput
                                amount={formData.motorInsuranceAmount}
                                frequency={formData.motorInsuranceFrequency}
                                onChange={handleMotorInsuranceChange}
                            />
                            <HomeInsuranceInput
                                amount={formData.homeInsuranceAmount}
                                frequency={formData.homeInsuranceFrequency}
                                onChange={handleHomeInsuranceChange}
                            />
                        </>
                    )}
                </div>

                {/* Mandatory Contributions */}
                <div className="expense-section">
                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('contributions')}>
                        <span>5. Mandatory Contributions (Monthly)</span>
                        <span className="toggle-icon">{expandedSections.contributions ? '▼' : '▶'}</span>
                    </h4>
                    {expandedSections.contributions && (
                        <>
                            <InputGroup label="Employee Provident Fund (EPF) Contribution" name="epf" value={formData.epf} onChange={handleChange} />
                            <InputGroup label="Voluntary Provident Fund (VPF) Contribution" name="vpf" value={formData.vpf} onChange={handleChange} />
                            <InputGroup label="Public Provident Fund (PPF)" name="ppf" value={formData.ppf} onChange={handleChange} />
                            <InputGroup label="National Pension System (NPS) - Tier I" name="nps_80ccd2" value={formData.nps_80ccd2} onChange={handleChange} />
                            <InputGroup label="Sukanya Samriddhi Yojana (SSY) for girl child" name="sukanyaSamriddhi" value={formData.sukanyaSamriddhi} onChange={handleChange} />
                        </>
                    )}
                </div>

                {/* Utilities */}
                <div className="expense-section">
                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('utilities')}>
                        <span>6. Utilities (Monthly)</span>
                        <span className="toggle-icon">{expandedSections.utilities ? '▼' : '▶'}</span>
                    </h4>
                    {expandedSections.utilities && (
                        <>
                            <InputGroup label="Electricity Bill" name="electricity" value={formData.electricity} onChange={handleChange} />
                            <InputGroup label="Water & Gas Bill" name="water" value={formData.water} onChange={handleChange} />
                            <InputGroup label="Internet & WiFi" name="internet" value={formData.internet} onChange={handleChange} />
                            <InputGroup label="OTT Subscriptions (Netflix, Amazon Prime, etc.)" name="ottSubscriptions" value={formData.ottSubscriptions} onChange={handleChange} />
                            <InputGroup label="Cable/DTH Connection" name="cable" value={formData.cable} onChange={handleChange} />
                        </>
                    )}
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => { updateData(formData); prevStep(); }} className="btn-secondary">← Back</button>
                    <button type="submit" className="btn-primary">Next: Living Expenses →</button>
                </div>
            </form>

            <style jsx>{`
                .salary-badge { background: #e0f2fe; padding: 10px 20px; border-radius: 8px; color: #0369a1; font-size: 1.1rem; margin-bottom: 20px; display: inline-block; }
                .expense-section { margin-bottom: 25px; padding: 15px; background: #f9fafb; border-radius: 8px; }
                .subsection-title { color: #4b5563; margin-bottom: 12px; font-size: 0.95rem; font-weight: 600; }
                .subsection-title.collapsible { cursor: pointer; user-select: none; display: flex; justify-content: space-between; align-items: center; padding: 10px; background: #fff; border-radius: 6px; transition: background-color 0.2s; }
                .subsection-title.collapsible:hover { background: #f3f4f6; }
                .toggle-icon { font-size: 0.8rem; color: #6b7280; }
            `}</style>
        </div>
    );
};

const InputGroup = ({ label, name, value, onChange }) => {
    const inputValue = value === null || value === undefined ? '' : String(value);
    return (
        <div className="form-group" style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '0.9rem', marginBottom: '4px', display: 'block' }}>{label}</label>
            <div className="input-with-icon" style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }}>₹</span>
                <input
                    type="number"
                    name={name}
                    value={inputValue}
                    onChange={onChange}
                    placeholder="0"
                    style={{ paddingLeft: '25px', width: '100%', padding: '8px 10px 8px 25px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                    onWheel={(e) => {
                        e.target.blur();
                        e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                        if (['e', 'E', '+', '-', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                            e.preventDefault();
                        }
                    }}
                />
            </div>
        </div>
    );
};

const LifeInsuranceInput = ({ amount, frequency, monthlyValue, onChange }) => {
    const frequencies = [
        { value: 'monthly', label: 'Monthly' },
        { value: 'quarterly', label: 'Quarterly' },
        { value: 'halfYearly', label: 'Half Yearly' },
        { value: 'annually', label: 'Annually' }
    ];

    return (
        <div className="form-group" style={{ marginBottom: '15px' }}>
            <label style={{ fontSize: '0.9rem', marginBottom: '8px', display: 'block', fontWeight: '500' }}>
                Life Insurance Premium
            </label>
            
            {/* Amount Input */}
            <div className="input-with-icon" style={{ position: 'relative', marginBottom: '10px' }}>
                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }}>₹</span>
                <input
                    type="number"
                    value={amount || ''}
                    onChange={(e) => onChange('lifeInsuranceAmount', e.target.value)}
                    placeholder="Enter premium amount"
                    style={{ paddingLeft: '25px', width: '100%', padding: '8px 10px 8px 25px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                    onWheel={(e) => {
                        e.target.blur();
                        e.stopPropagation();
                    }}
                />
            </div>
            
            {/* Frequency Radio Buttons */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                {frequencies.map((freq) => (
                    <label
                        key={freq.value}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '6px 10px',
                            borderRadius: '4px',
                            border: frequency === freq.value ? '2px solid #059669' : '1px solid #d1d5db',
                            backgroundColor: frequency === freq.value ? '#ecfdf5' : '#fff',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            color: frequency === freq.value ? '#059669' : '#4b5563'
                        }}
                    >
                        <input
                            type="radio"
                            name="lifeInsuranceFrequency"
                            value={freq.value}
                            checked={frequency === freq.value}
                            onChange={(e) => onChange('lifeInsuranceFrequency', e.target.value)}
                            style={{ margin: 0, marginRight: '8px', cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px' }}
                        />
                        <span>{freq.label}</span>
                    </label>
                ))}
            </div>
            
            {/* Monthly Equivalent Display */}
            {monthlyValue && (
                <div style={{ fontSize: '0.8rem', color: '#059669', background: '#f0fdf4', padding: '6px 10px', borderRadius: '4px' }}>
                    <strong>Monthly Equivalent: ₹{parseFloat(monthlyValue).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                </div>
            )}
        </div>
    );
};

// Health Insurance Input with Monthly/Annually Frequency
const HealthInsuranceInput = ({ amount, frequency, onChange }) => {
    const monthlyValue = parseFloat(amount) > 0 && frequency === 'annually' ? (parseFloat(amount) / 12).toFixed(2) : amount;
    
    return (
        <div className="form-group">
            <label>Health Insurance Premium (Self & Family)</label>
            <input 
                type="number" 
                value={amount} 
                onChange={(e) => onChange('healthInsuranceAmount', e.target.value)}
                placeholder="Enter amount"
                className="form-control"
            />
            
            <div className="frequency-options" style={{ marginTop: '8px', display: 'flex', gap: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input 
                        type="radio" 
                        name="healthInsuranceFrequency" 
                        value="monthly" 
                        checked={frequency === 'monthly'} 
                        onChange={(e) => onChange('healthInsuranceFrequency', e.target.value)}
                        style={{ margin: 0, marginRight: '6px', cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px' }}
                    />
                    <span>Monthly</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input 
                        type="radio" 
                        name="healthInsuranceFrequency" 
                        value="annually" 
                        checked={frequency === 'annually' || !frequency} 
                        onChange={(e) => onChange('healthInsuranceFrequency', e.target.value)}
                        style={{ margin: 0, marginRight: '6px', cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px' }}
                    />
                    <span>Annually</span>
                </label>
            </div>
            
            {monthlyValue > 0 && frequency === 'annually' && (
                <div style={{ fontSize: '0.8rem', color: '#059669', background: '#f0fdf4', padding: '6px 10px', borderRadius: '4px', marginTop: '8px' }}>
                    <strong>Monthly Equivalent: ₹{parseFloat(monthlyValue).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                </div>
            )}
        </div>
    );
};

// Property Tax Input with Monthly/Annually Frequency
const PropertyTaxInput = ({ amount, frequency, onChange }) => {
    const monthlyValue = parseFloat(amount) > 0 && frequency === 'annually' ? (parseFloat(amount) / 12).toFixed(2) : amount;
    
    return (
        <div className="form-group">
            <label>Property Tax</label>
            <input 
                type="number" 
                value={amount} 
                onChange={(e) => onChange('propertyTaxAmount', e.target.value)}
                placeholder="Enter amount"
                className="form-control"
            />
            
            <div className="frequency-options" style={{ marginTop: '8px', display: 'flex', gap: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input 
                        type="radio" 
                        name="propertyTaxFrequency" 
                        value="monthly" 
                        checked={frequency === 'monthly'} 
                        onChange={(e) => onChange('propertyTaxFrequency', e.target.value)}
                        style={{ margin: 0, marginRight: '6px', cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px' }}
                    />
                    <span>Monthly</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input 
                        type="radio" 
                        name="propertyTaxFrequency" 
                        value="annually" 
                        checked={frequency === 'annually' || !frequency} 
                        onChange={(e) => onChange('propertyTaxFrequency', e.target.value)}
                        style={{ margin: 0, marginRight: '6px', cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px' }}
                    />
                    <span>Annually</span>
                </label>
            </div>
            
            {monthlyValue > 0 && frequency === 'annually' && (
                <div style={{ fontSize: '0.8rem', color: '#059669', background: '#f0fdf4', padding: '6px 10px', borderRadius: '4px', marginTop: '8px' }}>
                    <strong>Monthly Equivalent: ₹{parseFloat(monthlyValue).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                </div>
            )}
        </div>
    );
};

// Motor Vehicle Insurance Input with Monthly/Annually Frequency
const MotorInsuranceInput = ({ amount, frequency, onChange }) => {
    const monthlyValue = parseFloat(amount) > 0 && frequency === 'annually' ? (parseFloat(amount) / 12).toFixed(2) : amount;
    
    return (
        <div className="form-group">
            <label>Motor Vehicle Insurance</label>
            <input 
                type="number" 
                value={amount} 
                onChange={(e) => onChange('motorInsuranceAmount', e.target.value)}
                placeholder="Enter amount"
                className="form-control"
            />
            
            <div className="frequency-options" style={{ marginTop: '8px', display: 'flex', gap: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input 
                        type="radio" 
                        name="motorInsuranceFrequency" 
                        value="monthly" 
                        checked={frequency === 'monthly'} 
                        onChange={(e) => onChange('motorInsuranceFrequency', e.target.value)}
                        style={{ margin: 0, marginRight: '6px', cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px' }}
                    />
                    <span>Monthly</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input 
                        type="radio" 
                        name="motorInsuranceFrequency" 
                        value="annually" 
                        checked={frequency === 'annually' || !frequency} 
                        onChange={(e) => onChange('motorInsuranceFrequency', e.target.value)}
                        style={{ margin: 0, marginRight: '6px', cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px' }}
                    />
                    <span>Annually</span>
                </label>
            </div>
            
            {monthlyValue > 0 && frequency === 'annually' && (
                <div style={{ fontSize: '0.8rem', color: '#059669', background: '#f0fdf4', padding: '6px 10px', borderRadius: '4px', marginTop: '8px' }}>
                    <strong>Monthly Equivalent: ₹{parseFloat(monthlyValue).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                </div>
            )}
        </div>
    );
};

// Home Insurance Input with Monthly/Annually Frequency
const HomeInsuranceInput = ({ amount, frequency, onChange }) => {
    const monthlyValue = parseFloat(amount) > 0 && frequency === 'annually' ? (parseFloat(amount) / 12).toFixed(2) : amount;
    
    return (
        <div className="form-group">
            <label>Home Insurance</label>
            <input 
                type="number" 
                value={amount} 
                onChange={(e) => onChange('homeInsuranceAmount', e.target.value)}
                placeholder="Enter amount"
                className="form-control"
            />
            
            <div className="frequency-options" style={{ marginTop: '8px', display: 'flex', gap: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input 
                        type="radio" 
                        name="homeInsuranceFrequency" 
                        value="monthly" 
                        checked={frequency === 'monthly'} 
                        onChange={(e) => onChange('homeInsuranceFrequency', e.target.value)}
                        style={{ margin: 0, marginRight: '6px', cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px' }}
                    />
                    <span>Monthly</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input 
                        type="radio" 
                        name="homeInsuranceFrequency" 
                        value="annually" 
                        checked={frequency === 'annually' || !frequency} 
                        onChange={(e) => onChange('homeInsuranceFrequency', e.target.value)}
                        style={{ margin: 0, marginRight: '6px', cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px' }}
                    />
                    <span>Annually</span>
                </label>
            </div>
            
            {monthlyValue > 0 && frequency === 'annually' && (
                <div style={{ fontSize: '0.8rem', color: '#059669', background: '#f0fdf4', padding: '6px 10px', borderRadius: '4px', marginTop: '8px' }}>
                    <strong>Monthly Equivalent: ₹{parseFloat(monthlyValue).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                </div>
            )}
        </div>
    );
};

export default SalaryIncomeForm;
